import React, { useMemo, useState } from "react";
import { EQUIPMENT_CATEGORIES, EQUIPMENT_PRESETS } from "../../config/equipment";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { X } from "lucide-react";
import { ChoiceChip } from "./ProfileParts";

const KNOWN = new Set(
  Object.values(EQUIPMENT_CATEGORIES)
    .flat()
    .map((item) => item.toLowerCase()),
);

/**
 * Every item, grouped by category as wrapped chips, so nothing hides in a
 * collapsed section. Matching is case-insensitive; items that aren't in the
 * catalogue show under "Your own".
 */
const EquipmentPicker = ({ selectedEquipment = [], onChange }) => {
  const [customInput, setCustomInput] = useState("");

  const selectedSet = useMemo(
    () => new Set(selectedEquipment.map((e) => e.toLowerCase())),
    [selectedEquipment],
  );
  const isSelected = (item) => selectedSet.has(item.toLowerCase());
  const own = selectedEquipment.filter((e) => !KNOWN.has(e.toLowerCase()));

  const toggle = (item) =>
    onChange(
      isSelected(item)
        ? selectedEquipment.filter((e) => e.toLowerCase() !== item.toLowerCase())
        : [...selectedEquipment, item],
    );

  const addCustom = () => {
    const trimmed = customInput.trim();
    if (trimmed && !isSelected(trimmed)) onChange([...selectedEquipment, trimmed]);
    setCustomInput("");
  };

  return (
    <div className="grid gap-4">
      <div className="grid gap-2">
        <span className="text-xs text-muted-foreground">Start from (replaces the list)</span>
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(EQUIPMENT_PRESETS).map((name) => (
            <ChoiceChip
              key={name}
              selected={false}
              onClick={() => onChange([...EQUIPMENT_PRESETS[name]])}
            >
              {name}
            </ChoiceChip>
          ))}
        </div>
      </div>

      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold">
          {selectedEquipment.length > 0
            ? `${selectedEquipment.length} selected`
            : "Nothing selected"}
        </span>
        {selectedEquipment.length > 0 && (
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            onClick={() => onChange([])}
          >
            Clear all
          </button>
        )}
      </div>

      {Object.entries(EQUIPMENT_CATEGORIES).map(([category, items]) => (
        <div key={category} className="grid gap-1.5">
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {category}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {[...new Set(items)].map((item) => (
              <ChoiceChip key={item} selected={isSelected(item)} onClick={() => toggle(item)}>
                {item}
              </ChoiceChip>
            ))}
          </div>
        </div>
      ))}

      {own.length > 0 && (
        <div className="grid gap-1.5">
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Your own
          </span>
          <div className="flex flex-wrap gap-1.5">
            {own.map((item) => (
              <ChoiceChip
                key={item}
                selected
                onClick={() => toggle(item)}
                className="inline-flex items-center gap-1.5"
              >
                <span className="sr-only">Remove </span>
                {item}
                <X className="h-3.5 w-3.5 opacity-70" />
              </ChoiceChip>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
        <Input
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder="Add something else"
          aria-label="Add your own equipment"
        />
        <Button type="button" variant="outline" onClick={addCustom} disabled={!customInput.trim()}>
          Add
        </Button>
      </div>
    </div>
  );
};

export default EquipmentPicker;
